import React, { useMemo } from 'react';
import { getEstadoChipClass, getMotivoChipClass } from '../../utils/styles';

/**
 * CardPoliza - Componente para mostrar el resumen de una póliza.
 * Sincronizado con permisos de Solo Lectura: Deshabilita de forma atómica y visual Editar, Anular, Eliminar, Rehabilitar e Ir a la Gestión Completa.
 */
const CardPoliza = ({ 
    poliza, 
    onView, 
    onEdit, 
    onDelete, 
    onAnular, 
    onNavigate, 
    onRehabilitar,
    isAnularDisabled = false,
    isFullUser = true // ◄ Recibido por Props de forma síncrona
}) => {
  const hasFullAccess = isFullUser;
  
  // --- LÓGICA DE ESTADO DINÁMICO POR FECHA ---
  const displayStatus = useMemo(() => {
    if (!poliza) return '---';
    
    const estadoOriginal = (poliza.estadoEmision || '').toUpperCase();
    
    // 1. Si está anulada manualmente, respetamos ese estado por sobre las fechas
    if (estadoOriginal === 'ANULADA') return 'ANULADA';

    // ◄◄◄ ESCUDO DE PROTECCIÓN DE ESTADOS PENDIENTES
    const noCalcular = [
      'PENDIENTE DE EMISIÓN',
      'PENDIENTE DE INSPECCIÓN',
      'PROPUESTA INGRESADA'
    ].includes(estadoOriginal);

    if (noCalcular) return estadoOriginal;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = poliza.inicioVigencia ? new Date(poliza.inicioVigencia + "T00:00:00") : null;
    const end = poliza.finVigencia ? new Date(poliza.finVigencia + "T00:00:00") : null;

    // 2. Verificación de Vencimiento (Prioridad: Si hay fin y ya pasó)
    if (end && today > end) return 'VENCIDA';

    // 3. Verificación de Inicio (Si hay inicio y aún no llegó)
    if (start && today < start) return 'NO INICIADA';

    // 4. Si tiene al menos una fecha y no es ninguna de las anteriores, está vigente
    if (start || end) return 'VIGENTE';

    // 5. Fallback a estado de base de datos si no hay fechas
    return estadoOriginal || 'PENDIENTE';
  }, [poliza]);

  // --- LÓGICA DE PATENTES (CONVIVENCIA FORMATO VIEJO Y NUEVO) ---
  const displayPatentes = useMemo(() => {
      if (Array.isArray(poliza?.patentes) && poliza.patentes.length > 0) {
          const validas = poliza.patentes.filter(p => p && String(p).trim() !== '');
          if (validas.length > 0) return validas.join(' • ');
      }
      if (poliza?.patente && typeof poliza.patente === 'string') {
          return poliza.patente.trim();
      }
      return null;
  }, [poliza]);

  // --- Alertas ---
  const checkDebtAlert = () => {
    if (poliza?.tieneDeuda) {
        return (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 flex items-center gap-1 border border-red-200 animate-pulse shadow-sm shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                Póliza con Deuda
            </span>
        );
    }
    return null;
  };

  const checkSiniAlert = () => {
    if (poliza?.tieneSiniestro) {
        return (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 flex items-center gap-1 border border-red-200 animate-pulse shadow-sm shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                Póliza con Siniestro
            </span>
        );
    }
    return null;
  };

  const checkPackAlert = () => {
    if (poliza?.isPolizaPack) {
        const ref = poliza.referenciasPolizaPack?.length > 0 
            ? poliza.referenciasPolizaPack.join(', ') 
            : (poliza.referenciaPolizaPack || 'S/D');

        return (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-800 flex items-center gap-1 border border-orange-200 shadow-sm shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Póliza Pack: {ref}
            </span>
        );
    }
    return null;
  };

  const checkProximityAlert = () => {
    if (poliza && displayStatus === 'VIGENTE' && poliza.finVigencia) {
        const today = new Date();
        today.setHours(0,0,0,0);
        const finDate = new Date(poliza.finVigencia + "T00:00:00");
        const diffTime = finDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays >= 0 && diffDays <= 30) {
            return (
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 flex items-center gap-1 border border-amber-200 shadow-sm shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    Vence en {diffDays} días
                </span>
            );
        }
    }
    return null;
  };

  const formatDate = (dateString) => {
      if(!dateString) return '-';
      const parts = dateString.split('-');
      if (parts.length === 3) {
          const [y, m, d] = parts;
          return `${d}/${m}/${y}`;
      }
      return dateString;
  };

  const isAnulada = displayStatus === 'ANULADA';
  
  // --- LIMITACIÓN OPERATIVA DE ACCIONES POR LECTURA O ANULACIÓN ---
  const navigateRestricted = !hasFullAccess;
  const editRestricted = isAnulada || !hasFullAccess;
  const anularRestricted = isAnulada || isAnularDisabled || !hasFullAccess;
  const deleteRestricted = !hasFullAccess;
  const rehabRestricted = !hasFullAccess;

  return (
    <div className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 overflow-hidden flex flex-col relative text-left">
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${isAnulada ? 'bg-red-400' : displayStatus === 'VIGENTE' ? 'bg-green-500' : displayStatus === 'NO INICIADA' ? 'bg-blue-400' : 'bg-gray-300'}`}></div>

      <div className="p-6 flex-grow pl-8">
        <div className="flex justify-between items-start mb-1 text-left">
            <div className="flex items-center gap-2 truncate max-w-[60%] text-left">
                <h4 className="text-sm font-bold text-indigo-600 uppercase tracking-wider shrink-0">
                    {poliza?.compania || '---'}
                </h4>
                {(poliza?.clienteNombreCompleto || poliza?.clienteNombre) && (
                    <>
                        <span className="text-slate-300 font-light">-</span>
                        <span className="text-xs font-medium text-slate-400 uppercase truncate">
                            {`${poliza.clienteNombre || ''} ${poliza.clienteApellido || ''}`.trim() || 'S/D'}
                        </span>
                    </>
                )}
            </div>
            
            <div className="flex items-center gap-2 shrink-0">
                {poliza?.motivo && (
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getMotivoChipClass(poliza.motivo)}`}>
                        {poliza.motivo}
                    </span>
                )}
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border shadow-sm ${getEstadoChipClass(displayStatus)}`}>
                    {displayStatus}
                </span>
            </div>
        </div>

        <h2 className="text-xl font-extrabold text-gray-900 mb-3 truncate text-left" title={poliza?.riesgo}>
            {poliza?.riesgo || 'Sin Riesgo'}
        </h2>

        <div className="flex flex-wrap items-center gap-3 mb-4 text-left">
            <div className="flex items-center bg-gray-100 px-3 py-1.5 rounded-md shadow-inner text-sm font-bold">
                <span className="text-gray-800">Póliza #{poliza?.nPoliza || '---'}</span>
                {poliza?.polizaExpirante && (
                    <span className="ml-2 pl-2 border-l border-gray-300 text-[10px] text-slate-400 italic">
                        EXP: #{poliza.polizaExpirante}
                    </span>
                )}
            </div>
            {displayPatentes && (
                <div className="bg-gray-100 text-gray-600 font-bold px-3 py-1.5 rounded-md text-sm uppercase shadow-inner border border-gray-200">
                    {displayPatentes}
                </div>
            )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5 text-left">
            <div className="flex items-center gap-2">
                {checkPackAlert()}
                {checkDebtAlert()}
                {checkSiniAlert()}
                {checkProximityAlert()}
            </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-100 items-center">
    
            {/* COLUMNA 1: Canal Comercial Principal */}
            <div>
                <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider mb-1">
                    Canal Comercial
                </p>
                <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-800 rounded-lg text-xs font-bold border border-blue-200 shrink-0">
                        {poliza?.canal || '-'}
                    </span>
                    <span className="text-xs font-semibold text-gray-600 truncate">
                        {poliza?.canalNombre || poliza?.subcanal || '-'}
                    </span>
                </div>
            </div>

            {/* COLUMNA 2: Subcanal (Solo si existe, sino muestra guión elegante) */}
            <div>
                <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider mb-1">
                    Subcanal
                </p>
                <p className="text-xs font-bold text-gray-700 truncate">
                    {poliza?.subcanalNombre || '-'}
                </p>
            </div>

            {/* COLUMNA 3: Vigencia Tipo Rango (Horizontal y Alineada) */}
            <div>
                <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wider mb-1">
                    Período de Vigencia
                </p>
                <div className="inline-flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-200/80 text-xs font-mono font-bold">
                    <span className="text-gray-700">{formatDate(poliza?.inicioVigencia)}</span>
                    <span className="text-gray-300">→</span>
                    <span className={isAnulada ? 'text-gray-400 line-through' : 'text-rose-600'}>
                        {formatDate(poliza?.finVigencia)}
                    </span>
                </div>
            </div>

        </div>
      </div>

      {/* Footer de Acciones */}
      <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center gap-4">
            {/* BOTÓN IR A GESTIÓN COMPLETA */}
            <button 
                onClick={() => !navigateRestricted && onNavigate && onNavigate(poliza)} 
                disabled={navigateRestricted}
                className={`mr-auto transition-all duration-200 p-1 rounded-full border shadow-sm ${
                    !navigateRestricted 
                        ? 'text-indigo-600 hover:text-indigo-800 hover:scale-110 bg-indigo-50 border-indigo-100 shadow-sm' 
                        : 'text-gray-300 bg-gray-100 border-gray-200 cursor-not-allowed opacity-20 pointer-events-none grayscale'
                }`} 
                title={navigateRestricted ? "Gestión completa no disponible en modo lectura" : "Ir a Gestión Completa de Póliza"}
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
            </button>

            {/* BOTÓN VER DETALLE (Siempre disponible para lectura) */}
            <button onClick={() => onView(poliza)} className="text-gray-400 hover:text-blue-600 hover:scale-110 transition-all duration-200" title="Ver Detalle">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            </button>

            {/* BOTÓN EDITAR */}
            <button 
                onClick={() => !editRestricted && onEdit(poliza)} 
                disabled={editRestricted}
                className={`transition-all duration-200 ${
                    !editRestricted 
                        ? 'text-gray-400 hover:text-yellow-500 hover:scale-110' 
                        : 'text-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                }`}
                title={editRestricted ? "Edición no disponible" : "Editar"}
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z" />
                </svg>
            </button>

            {/* BOTÓN REHABILITAR */}
            {isAnulada && (
                <button 
                    onClick={() => !rehabRestricted && onRehabilitar && onRehabilitar(poliza)} 
                    disabled={rehabRestricted}
                    className={`transition-all duration-200 ${
                        !rehabRestricted 
                            ? 'text-emerald-500 hover:text-emerald-700 hover:scale-110' 
                            : 'text-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                    }`} 
                    title={rehabRestricted ? "Rehabilitar Póliza" : "Rehabilitación no disponible en modo lectura"}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                </button>
            )}

            {/* BOTÓN ANULAR */}
            <button 
                onClick={() => !anularRestricted && onAnular(poliza)} 
                disabled={anularRestricted} 
                className={`transition-all duration-200 ${
                    !anularRestricted 
                        ? 'text-gray-400 hover:text-red-500 hover:scale-110' 
                        : 'text-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                }`} 
                title={anularRestricted ? "Anulación no disponible" : "Anular Póliza"}
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
            </button>

            {/* BOTÓN ELIMINAR */}
            <button 
                onClick={() => !deleteRestricted && onDelete(poliza)} 
                disabled={deleteRestricted}
                className={`transition-all duration-200 ${
                    !deleteRestricted 
                        ? 'text-gray-400 hover:text-red-600 hover:scale-110' 
                        : 'text-gray-200 cursor-not-allowed opacity-20 pointer-events-none'
                }`} 
                title={deleteRestricted ? "Eliminación no disponible" : "Eliminar Definitivamente"}
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
            </button>
      </div>
    </div>
  );
};

export default CardPoliza;